import axios, { AxiosHeaders, Method, InternalAxiosRequestConfig } from "axios"

export const baseURL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_ALPHA_BASE_LOCAL_URL
    : process.env.REACT_APP_ALPHA_BASE_URL

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30000,
})

// Multi-branch: attach the currently-selected branch to every request so the
// backend can scope data to it. Read from localStorage (persisted by the branch
// slice) since this runs outside React. Value "all" = All Branches (admin only).
export const CURRENT_BRANCH_KEY = "currentBranchId"
export const IMPERSONATION_TOKEN_KEY = "impersonationToken"

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const impersonationToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
      if (impersonationToken) {
        config.headers.set("Authorization", `Bearer ${impersonationToken}`)
      }

      // Single-campus model: send the selected campus. Ignore a stale "all"
      // (the "All Campuses" option was removed); when nothing is set yet the
      // backend falls back to the user's home campus.
      const branchId = impersonationToken
        ? sessionStorage.getItem(CURRENT_BRANCH_KEY)
        : localStorage.getItem(CURRENT_BRANCH_KEY)
      if (branchId && branchId !== "all") config.headers.set("x-branch", branchId)
    } catch {
      /* localStorage unavailable — ignore */
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const status = error.response?.status
    
    if (status === 401) {
      
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

const apiClient = async (props: {
  method?: Method
  url: string
  data?: Object | FormData
  headers?: AxiosHeaders
  params?: Object
}) => {
  const { method = "post", url, data, headers, params } = props

  return await axiosInstance({
    method,
    url,
    data,
    headers,
    params,
  })
}

export default apiClient
